import React, { useEffect, useRef, useState } from "react"
import { InformationCircleIcon } from "@heroicons/react/24/outline"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { navigate } from "vike/client/router"
import Stars from "../Stars"
import Spinner from "../Spinner"
import { Reactions } from "./Reactions"
import useT from "../../hooks/useT"
import { useMoods } from "../../utils/moods"
import { analyzeRatings } from "../../utils/ratings"
import { formatNumber } from "../../utils/productUtils"
import { LoadReviews, SubmitComment } from "../../storeApi"
import UserAvatar from "../UserAvatar"
import { requestSignIn } from "../../utils/signIn"

const REVIEWS_PER_LOAD = 10

function Mood({ label, num, total }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[15px]">{label}</span>
      <div className="h-2.5 w-full rounded bg-black/10 overflow-hidden">
        <div className="h-full bg-primary" style={{ width: `${total ? (num * 100) / total : 0}%` }} />
      </div>
    </div>
  )
}

// Top three moods from product.user_tags as progress bars
function Moods({ product }) {
  const moods = useMoods()
  const pairs = Object.entries(product.user_tags || {}).sort((a, b) => b[1] - a[1])
  const total = pairs.reduce((s, [, n]) => s + n, 0)
  return pairs.slice(0, 3).map(([mood, num]) => {
    const info = moods.find(({ id }) => id === mood)
    return <Mood key={mood} label={info?.displayText ?? mood} num={num} total={total} />
  })
}

function MoodChip({ id }) {
  const moods = useMoods()
  const mood = moods.find((m) => m.id === id)
  if (!mood) return null
  return (
    <span className="inline-flex items-center gap-1 h-[32px] px-3 rounded-full border border-bordered text-[14px]">
      <span className="text-[18px] leading-none">{mood.emoji}</span>
      {mood.displayText}
    </span>
  )
}

const dateOf = (created) => (created ? new Date(created).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "")

// a reply from the seller or the store goes under their name, not the person's who typed it
const replyAuthor = (reply) => (reply.replyAs === "aldoo" ? "Aldoo" : reply.replyAs === "vendor" ? reply.org?.name || "" : reply.user?.name || "")

/** The comments under a review: the seller's answer marked as the seller's. */
function Replies({ replies }) {
  const t = useT()
  return (
    <div className="flex flex-col gap-3 ml-6 md:ml-12" data-testid="review-replies">
      {replies.map((reply) => {
        const name = replyAuthor(reply)
        return (
          <div key={reply._id} className={`flex flex-col gap-2 rounded-lg px-4 py-3 ${reply.replyAs ? "bg-primary/5 border-l-2 border-primary" : "bg-neutral/60"}`}>
            <div className="flex items-center gap-2">
              <UserAvatar user={reply.replyAs ? { name } : reply.user} size={32} />
              <div className="flex flex-col">
                <span className="flex items-center gap-2">
                  <Typography variant="txt_accent">{name}</Typography>
                  {reply.replyAs === "vendor" && <span className="text-[12px] text-text_secondary">{t`Seller`}</span>}
                </span>
                <Typography variant="txt2_dim">{dateOf(reply.created)}</Typography>
              </div>
            </div>
            <Typography variant="txt1" className="leading-relaxed whitespace-pre-line">
              {reply.text}
            </Typography>
          </div>
        )
      })}
    </div>
  )
}

/** Writing a comment under a review; `onSent(comment)` gets it as saved. */
function ReplyComposer({ review, onSent, onCancel }) {
  const t = useT()
  const [text, setText] = useState("")
  const [pending, setPending] = useState(false)
  const [error, setError] = useState("")

  const send = async () => {
    const body = text.trim()
    if (!body || pending) return
    setPending(true)
    setError("")
    try {
      const result = await SubmitComment(review._id, "UserReview", body)
      if (!result?._id || result.error) return setError(t`Your comment could not be sent. Please try again.`)
      onSent(result)
      setText("")
    } catch {
      setError(t`Your comment could not be sent. Please try again.`)
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="flex flex-col gap-2 ml-6 md:ml-12" data-testid="review-reply-composer">
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        maxLength={4000}
        disabled={pending}
        autoFocus
        placeholder={t`Write a comment…`}
        className="w-full rounded-md border border-bordered bg-paper p-3 text-[15px] focus:outline-none focus:border-primary"
        data-testid="review-reply-text"
      />
      {error && <span className="text-[14px] text-error">{error}</span>}
      <div className="flex gap-2">
        <Button variant="primary" onClick={send} disabled={pending || !text.trim()} data-testid="review-reply-send">
          {t`Submit`}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          {t`Cancel`}
        </Button>
      </div>
    </div>
  )
}

function ReviewBox({ review }) {
  const t = useT()
  const [auth] = PlatformAuth.useAuth()
  const [replies, setReplies] = useState(review.replies || [])
  const [composing, setComposing] = useState(false)
  useEffect(() => setReplies(review.replies || []), [review])
  const { user, tags = [], text, rating, created } = review
  if (!user) return null
  const date = dateOf(created)
  return (
    <article className="flex flex-col max-w-[900px] soft_card gap-4 p-4 mb-4">
      <div className="flex flex-row items-center gap-4">
        <UserAvatar user={user} size={46} />
        <div className="flex flex-col justify-center">
          <div className="flex gap-2">
            <Typography variant="txt_accent">
              {user.name}
            </Typography>
            <Stars value={rating} size={14} />
          </div>

          <Typography variant="txt2_dim">
            {date}
          </Typography>
          
        </div>
      </div>

      {/* {tags.length > 0 && (
        <div className="flex flex-row flex-wrap gap-2 mb-8">
          {tags.map((id) => (
            <MoodChip key={id} id={id} />
          ))}
        </div>
      )} */}

      {text && <Typography variant="txt1" className="leading-relaxed">
                  {text} 
                </Typography>
      }
     
      {replies.length > 0 && <Replies replies={replies} />}

      {/* a signed-in visitor may comment under a review; a guest is asked to sign in first */}
      {!composing && (
        <button
          type="button"
          onClick={() => (auth ? setComposing(true) : requestSignIn())}
          className="self-end text-[14px] font-semibold text-primary hover:underline"
          data-testid="review-reply-open"
        >
          {t`Reply`}
        </button>
      )}
      {composing && (
        <ReplyComposer
          review={review}
          onCancel={() => setComposing(false)}
          onSent={(comment) => {
            setReplies((current) => [...current, comment])
            setComposing(false)
          }}
        />
      )}

      {/* <Reactions target={review} targetModel="UserReview" /> */}
    </article>
  )
}

/**
 * Ratings & reviews block: "Rate" CTA for signed-in users, average + moods,
 * paged review list with reactions/comments.
 */
export default function ProductReviews({ product, reviewUrl }) {
  const t = useT()
  const [auth] = PlatformAuth.useAuth()
  const topRef = useRef(null)
  const { avgRating, numRatings } = analyzeRatings(product?.ratings)
  const [state, setState] = useState({ list: [], total: 0, page: 0, pending: false, loaded: false })

  const load = async (page, reset = false) => {
    setState((s) => ({ ...s, pending: true }))
    try {
      const result = await LoadReviews(product.pid, page === 0, page + 1, REVIEWS_PER_LOAD)
      const reviews = result?.my_review ? [result.my_review, ...(result.reviews || [])] : result?.reviews || []
      setState((s) => ({ list: reset ? reviews : [...s.list, ...reviews], total: result?.total ?? 0, page: page + 1, pending: false, loaded: true }))
    } catch {
      setState((s) => ({ ...s, pending: false, loaded: true }))
    }
  }

  // reloads when the product's rating count moves (a review was just left)
  useEffect(() => {
    if (!product?.pid) return
    load(0, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.pid, numRatings])

  // Scroll to the block on #reviews, also when the hash changes later (closing
  // a link to <product>#reviews arrives later)
  useEffect(() => {
    const onHash = () => {
      if (window.location.hash === "#reviews") topRef.current?.scrollIntoView({ behavior: "smooth" })
    }
    onHash()
    window.addEventListener("hashchange", onHash)
    return () => window.removeEventListener("hashchange", onHash)
  }, [])

  const { list, total, pending, loaded } = state
  const canRate = loaded && auth && (list.length === 0 || auth._id !== list[0]?.user?._id)

  return (
    <section ref={topRef} id="reviews" className="w-full max-w-[900px] mx-auto">
      {canRate && (
        <div className="flex flex-col items-center mb-[108px]">
          <Typography 
            component="h2" 
            variant="h4" 
          >
            {t`Did you like this product?`}
          </Typography>
          <Typography 
            variant="txt_dim" 
            className="block mb-6"
          >
            {t`Tell us what you think`}
          </Typography>
          <Button 
            variant="primary" 
            onClick={() => navigate(reviewUrl || `${window.location.pathname}/leave-review`)}
          >
            {t`Rate`}
          </Button>
        </div>
      )}

      {numRatings > 0 && (
        <div className="mb-5 md:mb-6">
          <div className="flex flex-col lg:flex-row items-center justify-center gap-4 mb-5 md:mb-6">
            <Typography 
              component="h2" 
              variant="h4"
            >
              {t`Ratings & Reviews`}
            </Typography>
          </div>

          <div className="flex flex-col items-center">
            <Typography 
              component="output"
              variant="title"
              className="font-condensed font-normal text-primary_hov tracking-tighter leading-none"
            >
              {avgRating.toFixed(1)}
            </Typography>
            <Stars value={avgRating} size={20} />
            <Typography 
              variant="txt1_dim" 
              className="block mt-1"
            >
              {t("%num-reviews% Reviews", formatNumber(numRatings))}
            </Typography>
          </div>

        </div>
      )}

      {list.map((review) => (
        <ReviewBox key={review._id} review={review} />
      ))}
      {pending && <Spinner />}
      {!pending && loaded && total > list.length && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => load(state.page)}>
            {t("Load %d more reviews", total - list.length)}
          </Button>
        </div>
      )}
    </section>
  )
}
